import TextArea from '@/molecules/TextArea.vue';
import defaultValidators from '@/helpers/default-validators';
import * as Yup from 'yup';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import TextInput from '@/molecules/TextInput.vue';
import McvScoreComponent from '@/parcours/flows/mcv/mcvScoreComponent.vue';
import RadioRowItem from '@/molecules/RadioRowItem.vue';
import McvStressScore from '@/parcours/flows/mcv/McvStressScore.vue';
import { usePreventionParcours } from '@/parcours/composables/usePreventionParcours';
import { calculateBmi } from '@/parcours/flows/shared/helper';
import { flowStore, updateFlowData } from '@/parcours/stores/flowStore';
import SpecialistSelector from '@/parcours/components/specialistSelector.vue';

const { update, getById } = usePreventionParcours('/cms/coordinator-patient-mcv-infos');

const YES_NO = [
  { label: 'Oui', value: true },
  { label: 'Non', value: false },
];

export const mcvFlow = withGlobalFlow({
  id: 'mcvFlow',

  basePath: '/mcvFlow/flow',

  exitRoute: '/mcvFlow',

  async onInit({ route }) {
    const id = route.params.reservationId;
    const data = await getById(id);
    return {
      ...data,
      ...(data?.bmi !== undefined && {
        bmi: calculateBmi(data?.size, data?.weight),
      }),
    };
  },

  steps: {
    // ── Informations de santé ─────────────────────────────────────────────────
    antecedentsSante: {
      type: 'form',
      title: 'Informations de santé',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'size',
            type: 'number',
            as: TextInput,
            label: 'Taille',
            secondIconHint: 'cm',
            rules: defaultValidators.number,
          },
          {
            id: 'weight',
            type: 'number',
            as: TextInput,
            label: 'Poids',
            secondIconHint: 'kg',
            rules: defaultValidators.number,
          },
          {
            id: 'diabetes',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Êtes-vous suivi pour un diabète ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'hypertension',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Êtes-vous ou avez-vous été suivi pour une hypertension artérielle ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'cholesterol',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Êtes-vous ou avez-vous été suivi pour un excès de cholestérol ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'stroke',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Avez-vous déjà fait un accident vasculaire cérébral ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'drugs_treatment',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Suivez-vous un traitement médicamenteux ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'family_heart_disease',
            type: 'radio-group',
            as: RadioRowItem,
            label:
              'Avez-vous connaissance de maladies cardiovasculaires précoces dans votre famille proche (<55 ans chez père et frère, <65 ans chez mère et sœur) ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
        ],
      },
    },

    // ── Traitement hypolipidémie (conditionnel : drugs_treatment === true) ────
    lipidTherapy: {
      type: 'form',
      title: 'Traitement médicamenteux',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'lipid_lowering_therapy',
            type: 'radio-group',
            as: RadioRowItem,
            label: "Prenez-vous un traitement pour l'hypolipidémie ?",
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
        ],
      },
    },

    // ── Habitudes de vie ──────────────────────────────────────────────────────
    habitudesVie: {
      type: 'form',
      title: 'Vos habitudes de vie',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'smoking',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Fumez-vous ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'physical_activity',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Pratiquez-vous une activité physique hebdomadaire ?',
            options: YES_NO,
            optionValue: 'value',
            optionLabel: (opt: any) => opt.label,
            rules: defaultValidators.radioGroup,
          },
        ],
      },
    },

    // ── Test de stress (lecture seule, uniquement si données disponibles) ─────
    stressScore: {
      type: 'component',
      title: 'Test de stress',
      component: McvStressScore,
      props: (data: any) => ({ data: data.flowData }),
    },

    // ── Steps existants ───────────────────────────────────────────────────────
    mesureAndAnalyse: {
      type: 'form',
      title: 'Mesures et analyses',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'bmi',
            type: 'number',
            as: TextInput,
            state: 'disabled',
            label: 'IMC',
            secondIconHint: 'kg/m²',
            rules: defaultValidators.number,
          },
          {
            id: 'abdominal_perimeter',
            type: 'number',
            as: TextInput,
            label: 'Périmètre abdominal',
            secondIconHint: 'cm',
            rules: defaultValidators.number,
          },
          {
            id: 'glycated_hemoglobin',
            type: 'number',
            as: TextInput,
            label: 'Hémoglobine Glyquée',
            secondIconHint: '%',
            rules: defaultValidators.number,
          },
        ],
      },
    },
    bilanLipidique: {
      type: 'form',
      title: 'Bilan lipidique',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'total_cholesterol',
            type: 'number',
            as: TextInput,
            label: 'Cholestérol Total',
            secondIconHint: 'mg/dL',
            rules: defaultValidators.number,
            warning: (value: any) => {
              // A fix
              const num = parseFloat(value);
              console.log(
                "!isNaN(num) && num < 100 ? 'Le taux est bas' : undefined;",
                !isNaN(num) && num < 100 ? 'Le taux est bas' : undefined,
              );
              return !isNaN(num) && num < 100 ? 'Le taux est bas' : undefined;
            },
          },
          {
            id: 'hdl',
            type: 'number',
            as: TextInput,
            label: 'HDL',
            secondIconHint: 'mg/dL',
            rules: defaultValidators.number,
          },
          {
            id: 'triglycerides',
            type: 'number',
            as: TextInput,
            label: 'Triglycérides',
            secondIconHint: 'mg/dL',
            rules: defaultValidators.number,
          },
          {
            id: 'ldl',
            type: 'number',
            as: TextInput,
            label: 'LDL',
            secondIconHint: 'mg/dL',
            rules: defaultValidators.number,
          },
          {
            id: 'no_hdl_cholesterol',
            type: 'number',
            state: 'disabled',
            as: TextInput,
            label: 'Cholestérol non-HDL',
            secondIconHint: 'mg/dL',
            rules: defaultValidators.number,
            warning: (value: any) => {
              const num = parseFloat(value);
              return !isNaN(num) && num < 0 ? 'La valeur ne peut pas être négative' : undefined;
            },
          },
        ],
      },
    },
    pressionArt: {
      type: 'form',
      title: 'Pression artérielle',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'systolic_blood_pressure',
            type: 'number',
            as: TextInput,
            label: 'Pression artérielle systolique (PAS)',
            secondIconHint: 'mm/Hg',
            rules: Yup.number()
              .typeError('Ce champ doit être un numéro')
              .required('Ce champ est obligatoire')
              .min(80, 'La valeur minimale est 80 mmHg'),
          },
          {
            id: 'diastolic_blood_pressure',
            type: 'number',
            as: TextInput,
            label: 'Pression artérielle diastolique (PAD)',
            secondIconHint: 'mm/Hg',
            rules: Yup.number()
              .typeError('Ce champ doit être un numéro')
              .required('Ce champ est obligatoire')
              .min(50, 'La valeur minimale est 50 mmHg'),
          },
        ],
      },
    },
    score: {
      type: 'component',
      title: 'Score',
      component: McvScoreComponent,
      props: (data: any) => ({ data }),
    },
    recommandation: {
      type: 'form',
      title: 'Recommandations',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'consultation_practitioner',
            type: 'radio-group',
            as: RadioRowItem,
            label: 'Je recommande à mon patient de consulter son médecin traitant',
            options: [
              { label: 'Dans le mois suivant cet entretien', value: 'IN_THE_MONTH' },
              {
                label: 'Le plus rapidement possible dans les prochains jours',
                value: 'AS_SOON_AS_POSSIBLE',
              },
              { label: 'Non', value: 'NO' },
            ],
            optionValue: 'value',
            optionLabel: (option: any) => option.label,
            rules: defaultValidators.radioGroup,
          },
          {
            id: 'blood_test',
            type: 'radio-group',
            as: RadioRowItem,
            label:
              "Je recommande à mon patient d'effectuer une prise de sang pour confirmer les résultats",
            options: [
              { label: 'Oui', value: 'true' },
              { label: 'Non', value: 'false' },
            ],
            optionValue: 'value',
            optionLabel: (option: any) => option.label,
            rules: defaultValidators.radioGroup,
          },
        ],
      },
    },
    preview: {
      id: 'other_specialists',
      type: 'component',
      title: 'Recommandations',
      description:
        "Je recommande au patient d'aller consulter le(s) professionnel(s) de santé suivant(s):",
      component: SpecialistSelector,
      props: {
        specialists: [
          { name: 'Nutritionniste', value: 'Nutritionniste' },
          { name: 'Diététicien', value: 'Diététicien' },
          { name: 'Cardiologue', value: 'Cardiologue' },
        ],
      },
    },
    lastComment: {
      type: 'form',
      title: 'Un dernier commentaire ?',
      description: 'Autre commentaire ou recommandation pour le patient',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'other_recommendations',
            type: 'textarea',
            placeholder: 'Votre commentaire ...',
            as: TextArea,
            rules: defaultValidators.textOpt,
          },
        ],
      },
    },
  },

  transitions: {
    antecedentsSante: {
      sideEffect: async ({ data }: { data: any }) => {
        await update(data?.id, data, false);
      },
      next: ({ data }: { data: any }) => {
        // Recalcule l'IMC immédiatement avec les nouvelles taille/poids
        updateFlowData({ bmi: calculateBmi(data.size, data.weight) });
        // Conditionnel : step lipide uniquement si traitement médicamenteux
        return data.drugs_treatment === true ? 'lipidTherapy' : 'habitudesVie';
      },
    },
    lipidTherapy: {
      sideEffect: async ({ data }: { data: any }) => {
        await update(data?.id, data, false);
      },
      next: 'habitudesVie',
    },
    habitudesVie: {
      sideEffect: async ({ data }: { data: any }) => {
        await update(data?.id, data, false);
        // Force le re-fetch de onInit à l'arrivée sur stressScore
        if (data.total_stress_score != null || data.eva_value != null) {
          flowStore.initialized = false;
        }
      },
      next: ({ data }: { data: any }) => {
        // Affiche les scores de stress uniquement si des données existent
        if (data.total_stress_score != null || data.eva_value != null) {
          return 'stressScore';
        }
        return 'mesureAndAnalyse';
      },
    },
    stressScore: { next: 'mesureAndAnalyse' },
    mesureAndAnalyse: { next: 'bilanLipidique' },
    bilanLipidique: { next: 'pressionArt' },
    pressionArt: { next: 'score' },
    score: { next: 'recommandation' },
    recommandation: { next: 'preview' },
    preview: { next: 'lastComment' },
    lastComment: { submit: true },
  },

  async onSubmit(data) {
    await update(data?.id, data);
  },
});
